import React from 'react';
import { createRefetchContainer, graphql } from 'react-relay';
import ReactTable from 'react-table';
import withFixedColumns from 'react-table-hoc-fixed-columns';
import ReactTooltip from 'react-tooltip';
import { faInfoCircle } from '@fortawesome/free-solid-svg-icons'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import SellerGroupModelListPagination from './SellerGroupModelListPagination';
import SearchResultHeader from './SearchResultHeader';
import RefreshDataSpinner from './RefreshDataSpinner';
import StockLabel from './StockLabel';
import Price from './Price';
import type AppConfig from '../domain/AppConfig';
import AppService from '../service/AppService';
import {Message} from '@aperza/ui';
import {isMobile} from 'react-device-detect';
import WebMediaQuery from './WebMediaQuery';
import SellerGroupModelListGroupSelection from './SellerGroupModelListGroupSelection';
import SellerGroupModelListFilter from './SellerGroupModelListFilter';
import { useState, useRef } from 'react';
import AppConfigService from '../service/AppConfigService';
const appConfig:AppConfig = AppService.appConfig();


const ReactTableFixedColumns = withFixedColumns(ReactTable);

// This is for 2 MAIN Columns (Seller Model Number + Price + Shipping)
const getMainColumnWidth = (rows, accessor, headerText) => {
  const maxWidth = 400;
  const magicSpacing = 10;

  // Unescape encodeURIComponent is to check japanese character as well
  let rowData = rows.map(row => (unescape(encodeURIComponent(`${row[accessor]}` || ""))).length)

  if (rowData === undefined || rowData.length === 0) {
    return; // use default size if there is no data available
  }

  const cellLength = Math.max(
    ...rowData,
    headerText.length
  );

  // Add 20px spacing for the help icons
  return Math.min(maxWidth, (cellLength * magicSpacing) + 20)
}

// This is for spec columns other than the 3 main columns
const getColumnWidth = (rows, specId, headerText) => {
  const maxWidth = 500;
  let magicSpacing = 8;

  let index = getSpecIndex(rows[0], specId);
  // Unescape encodeURIComponent is to check japanese character as well
  let rowData = rows
        .filter(row => row.specs[index])
        .map(row => {
          return (unescape(encodeURIComponent(row.specs[index].specValue || ""))).length
        });

  if (rowData === undefined || rowData.length === 0) {
    return; // use default size if there is no data available
  }

  const cellLength = Math.max(
    ...rowData,
    (unescape(headerText).length)
  );

  let columnWidth = cellLength * magicSpacing;
  return Math.min(maxWidth, ((columnWidth > 100)?columnWidth:100) );
}

const getSpecIndex = (row, value) => {
  let specs = row.specs;
  for (var i=0; i<specs.length; i++)
    if (specs[i].specId === value)
      return i;
  return false;
}


class SellerGroupModelList extends React.Component {

  getCurrentModel(models) {
    if (models !== undefined && models.length > 0) {

      // console.log('Identifying if the model table list contains this seller model: ', this.state.sellerModelId);
      let currentModelIndex = 0;
      for (const [modelIndex, model] of models.entries()) {
        if (model.sellerModelId === this.state.sellerModelId) {
          currentModelIndex = modelIndex;
        }
      }
      return models[currentModelIndex];
    }

    return null;
  }

  doesCurrentModelHaveSpecGroups(models) {
    if (this.getCurrentModel(models) == null) {
      return false;
    }
    let groupSpecs = this.getCurrentModel(models).groupSpecs
    return groupSpecs !== undefined && groupSpecs.length !== 0;
  }

  currentModelHasMultipleGroups(groups) {
    // console.log('@currentModelHasMultipleGroups groups: ', groups);
    return groups !== undefined && groups.length > 1;
  }

  createColumns(models) {

    // if there are no models matched, get the last known state of the columns
    if (!this.doesCurrentModelHaveSpecGroups(models) && (this.columns !== undefined && this.columns.length !== 0)) {
      // console.log('doesCurrentModelHaveGroupSpecGroups? ', this.doesCurrentModelHaveSpecGroups(models))
      // console.log('doesCurrentModelHaveGroupSpecGroups? columns', this.columns)
      // return last known columns;
      return this.columns;
    }

    this.columns = [{
      Header: <Message messageId="groupModelList.column.modelNumber" />,
      id: 'sellerModelNumber',
      accessor: d =>  d,
      Cell: props =>
        <React.Fragment>
            <WebMediaQuery desktopOnly={true}>
            <a className='number td-click' data-track='{"action":"aperza_send_to_item"}' href={`${appConfig.marketplaceWebHost}/${appConfig.langCode}/d/i/${props.value.sellerModelNumber}/${props.value.sellerModelId}.html`}>
              <p data-tip={props.value.allModelSpecsIsPresentInGroup ? props.value.sellerProductGroupName : ''}>{props.value.sellerModelNumber} {props.value.allModelSpecsIsPresentInGroup && <FontAwesomeIcon icon={faInfoCircle} />}</p>
            </a>
            </WebMediaQuery>
          <WebMediaQuery mobileOnly={true}>
            <a className='number td-click' data-track='{"action":"aperza_send_to_item"}' href={`${appConfig.marketplaceWebHost}/${appConfig.langCode}/d/i/${props.value.sellerModelNumber}/${props.value.sellerModelId}.html`}>
              {props.value.sellerModelNumber}
            </a>
            <p style={{display: 'inline-block', margin: '0px 0px 0px 8px'}} data-tip={props.value.allModelSpecsIsPresentInGroup ? props.value.sellerProductGroupName : ''}>{props.value.allModelSpecsIsPresentInGroup && <FontAwesomeIcon icon={faInfoCircle} />}</p>
          </WebMediaQuery>
          <ReactTooltip place='right' type='warning'/>
        </React.Fragment>,
      width: getMainColumnWidth(models, 'sellerModelNumber', '型式'),
      sortable: false,
      filterable: true,
        Filter: ({filter, onChange}) => (
          <div className="filterIcon">
            <input type={'text'} onChange={event => onChange(event.target.value)} />
            <i className="fa fa-filter"></i>
          </div>
        ),
      }, {
        Header: <Message messageId="groupModelList.column.price" />,
        id: 'price',
        accessor: d =>  d,
        Cell: props => <div style={{textAlign: "right"}}><Price currency={'JPY'} price={props.value.price} openPrice={props.value.openPrice} /></div>,
        width: getMainColumnWidth(models, 'lowestOfferPrice', 'lowestOfferPrice'),
        className: 'price',
        filterable: false,
        sortable: false
      }, {
        Header: <Message messageId="groupModelList.column.stocks" />,
        id: 'stocks',
        accessor: d =>  d,
        Cell: props => <div style={{textAlign: "center"}}><StockLabel numberOfStocks={props.value.stocks} salesMethodMaker={props.value.salesMethodMaker} salesMethodLabel={props.value.salesMethodLabel}/></div>,
        width: getMainColumnWidth(models, 'lowestOfferPrice', 'lowestOfferPrice'),
        filterable: false,
        sortable: false
      }, {
        Header: <Message messageId="groupModelList.column.shippingDate" />,
        // accessor: 'shippingDate',
        id: 'shippingDate',
        accessor: d =>  d,
        Cell: props => <div style={{textAlign: "center"}}>{props.value.shippingDate}</div>,
        width: getMainColumnWidth(models, 'shippingDate', 'Shipping Date'),
        filterable: false,
        sortable: false
    }];

    if (!isMobile) {
      // set first column (seller model number) to be fixed for DESKTOPS
      this.columns[0].fixed = 'left';
    }

    // console.log('groupspecs currentModel: ',  this.getCurrentModel(models))
    // console.log('groupspecs: ',  this.getCurrentModel(models).groupSpecs)
    let parentColumns = this.columns;

    let self = this;
    // console.log('filter suggestions are: ', self.state.filterSuggestions);
    this.getCurrentModel(models).groupSpecs.reduce(function(map, obj) {
      // console.log('Specs for specId. data of filterSuggestions is', self.state.filterSuggestions);
      let availableSpecsForSpecId = [];
      for (let i = 0; i < self.state.filterSuggestions.length; i ++) {
        if (self.state.filterSuggestions[i].specId === obj.specId) {
          availableSpecsForSpecId = self.state.filterSuggestions[i].specValues;
          break;
        }
      }

      parentColumns.push({
        Header: obj.specName,
        id: obj.specId + '',  // concatenate string as id need to be of string type
        accessor: d => {
          let specsRowMap = d.specs.reduce(function(map, obj) {
            map[obj.specId] = obj.specValue;
            return map;
          }, {});
          return  specsRowMap[obj.specId]
        },
        Cell: row => (<div className={"spec_"+obj.specId} style={{textAlign: "center"}}>{row.value}</div>),
//        width: getColumnWidth(models, obj.specId, obj.specName),
        sortable: false,
        filterable: true,
        Filter: ({filter, onChange}) => (
          <SellerGroupModelListFilter filter={filter} onChangeFromSpecTable={onChange} specId={obj.specId} filterSuggestions={availableSpecsForSpecId}/>
        ),
      });

    }, {});

    // console.log('columns', this.columns);

    return this.columns;
  }


  constructor(props) {
    super(props);
    this.columns = [];
    this.cachedRequestSpecs = [];
    this.state = {
      loading: true,
      data: this.props.searchSeller.searchSellerGroup,
      sellerContactId:  this.props.sellerContactId,
      sellerModelId:  this.props.sellerModelId,
      sellerProductGroupId:  null,
      filtered: [],
      limit: 10,
      page: 0,
      filterSuggestions: [],
    };
  }

  render() {
    // console.log('SellerGroupModelList: @render-state:', this.state);
    // console.log('SellerGroupModelList: @render-sellers:', this.props.searchSeller.searchSellerGroup.salesMethodMaker);
    // console.log('SellerGroupModelList: @render-store:', this.props.relay.environment.getStore());

    let data = this.props.searchSeller.searchSellerGroup;
    if (!data) {
      // console.log('No group data found for this seller model', this.state)
      return (null);
    }

    const models = data.models;
    const pageMetadata = data.pageMetadata;
    const groups = data.groups;

    // console.log('Models, ',models);

    let reactTable = null;
    return (
      <div>
        <SearchResultHeader pageMetadata={pageMetadata} updateLimit={this.updateLimit} />
        {this.currentModelHasMultipleGroups(groups) && <SellerGroupModelListGroupSelection groups={groups} changeGroup={this.changeGroup}></SellerGroupModelListGroupSelection>}
        <ReactTableFixedColumns
          loadingText={<Message messageId="groupModelList.loadingText" />}
          LoadingComponent={RefreshDataSpinner}
          noDataText={<Message messageId="groupModelList.noDataText" />}
          filterable
          manual
          data={models}
          PaginationComponent={SellerGroupModelListPagination}
          resizable={!isMobile}   // not resizable for mobile
          pages={pageMetadata.noOfPages}
          columns={this.createColumns(models)}
          pageSize={this.state.limit}
          onFetchData={this._refetch}
          loading={this.state.loading} // Display the loading overlay when we need it
          className="-highlight"
          onFilteredChange={(filtered) => {

            if (filtered !== undefined && filtered.length > 0) {
              let specIdAndValues = filtered.map(filter => ({ specId: filter.id, specValue: filter.value }));
              let specIdAndValueKey = filtered.map(filter => filter.value + "|" + filter.id).reduce((accumulator, b) => (accumulator + "-" + b));

              // add a cache since onFocus triggers, this event and we don't want to make ajax call
              if (!this.cachedRequestSpecs.includes(specIdAndValueKey)) {
                // console.log('@@ fetching data', specIdAndValueKey)
                this.fetchPossibleSpecValues(specIdAndValues)
                this.cachedRequestSpecs.push(specIdAndValueKey);
              } else {
                // console.log('@@ NOT fething data, already cached', specIdAndValueKey)
              }
            } else {
              // console.log('no filter defined, fetching all');
              this.fetchPossibleSpecValues([]);
            }
          }}
        />
      </div>
    );
  }

  fetchPossibleSpecValues = (specIdAndValues) => {
    // console.log('Fetching possible values with params...', specIdAndValues)

    // ***************************** FETCH
    let apiHost = AppConfigService.getApiHostname();

    fetch(`${apiHost}/v2/product/seller/spec/uniqueSpecs`, {
      method: 'post',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify({
        sellerModelId: this.state.sellerModelId,
        sellerContactId: this.state.sellerContactId,
        specIdAndValues: specIdAndValues
      }),
      // cache: "force-cache"
      // cache: "no-store"
    })
      .then(res => res.json())
      .then(
        (result) => {
          // console.log('Fetching possible values result: ...', JSON.stringify(result.data))
          if (result && result.data) {
            this.setState({
              isLoaded: true,
              filterSuggestions: result.data.specIdAndValues
            });
          } else {
            console.warn(`unable to fetch specData for request (with 200 response): ${jsonBody}`)
          }
        },
        // Note: it's important to handle errors here
        // instead of a catch() block so that we don't swallow
        // exceptions from actual bugs in components.
        (error) => {
          console.error(`unable to fetch specData for request: ${jsonBody}`)
          this.setState({
            isLoaded: true,
            error
          });
        })
  }

  componentDidMount(): void {
    this._refetch({filtered:[], page: 0, limit: this.state.limit});
    this.fetchPossibleSpecValues();
  }

  updateLimit = (limit) => {
    // console.log(`SellerGroupModelList: @updateLimit: currentLimit=${this.state.limit}, limit=${limit}'`);
    this.setState({limit: parseInt(limit)});
  }

  changeGroup = (sellerProductGroupId) => {
    // console.log(`SellerGroupModelList: @changeGroup: from: sellerProductGroupId=${this.state.sellerProductGroupId} to sellerProductGroupId=${sellerProductGroupId}`);
    this.setState({sellerProductGroupId: sellerProductGroupId});
  }

  componentDidUpdate(prevProps, prevState, snapshot) {
    // console.log(`Component did update? prevState.limit=${prevState.limit}, currentState.limit=${this.state.limit}`);
    // console.log(`Component did update? prevState.page=${prevState.page}, currentState.page=${this.state.page}`);
    if (this.state.limit !== prevState.limit ||
        this.state.sellerProductGroupId !== prevState.sellerProductGroupId) {
          // prevState.page = 0;
          // this.setState({page: 0, limit: this.state.limit});     // initialize to 0 when selecting new limit or group
          this._refetch(this.state);    // state is already updated
    }
  }


  _refetch = (state, instance) => {
    // console.log('SellerGroupModelList: @refetch-state', state);

    let selectedFilters = {};
    let specs = [];

    for (let filter of state.filtered) {
      if (Number.isInteger(parseInt(filter.id, 10))) {
          // all spec id's are number based, all other fields are based on name
          specs.push({specId: filter.id, specValue: filter.value.trim()});
      } else {
          selectedFilters[filter.id] = filter.value.trim();
      }
    }

    if (specs.length) {
      // there is a spec filter
      selectedFilters.specs = specs;
    }

    let selectedSellerGroupId = state.sellerProductGroupId === undefined ? this.state.sellerProductGroupId : state.sellerProductGroupId;
    let parsedLimit = state.limit ? state.limit : this.state.limit;  // use default state if no limit passed
    // console.log('limit to use,', parsedLimit)
    // console.log('Using group from provided state if available, otherwise pick the last state avaialble: ', selectedSellerGroupId)
    // console.log('page to use: ', state.page)
    this.setState({ loading: true});


    // console.log('preparing to fetch new data with limit (old state): ', this.state.limit)
    // console.log('preparing to fetch new data with limit (current state): ', state.limit)
    this.props.relay.refetch(
      {
        sellerContactId: this.state.sellerContactId,
        sellerModelId: this.state.sellerModelId,
        sellerProductGroupId: parseInt(selectedSellerGroupId),
        sellerModelNumber: selectedFilters['sellerModelNumber'],
        specs: JSON.stringify(specs),
        offset: (state.page + 1), // ui-state page is 0-based, api is 1-based (add 1 when sending graphQL request)
        limit: parsedLimit,
        stocks: selectedFilters['stocks'],
        price: selectedFilters['price'],
        shippingDate: selectedFilters['shippingDate'],
        //... selectedFilters,
      },
      null,  // We can use the refetchVariables as renderVariables
      (error) => {
        // console.log('SellerGroupModelList: @refetch-done', state);
        this.setState({ loading: false});
      },
      { force: false }  // Assuming we've configured a network layer cache, we want to ensure we fetch the latest data.
    );
  };
}

export default createRefetchContainer(
  SellerGroupModelList,
  {
    searchSeller: graphql`
        fragment SellerGroupModelList_searchSeller on SearchSeller
        @argumentDefinitions(
            sellerContactId: {type: "String"},
            sellerModelId: {type: "String"},
            sellerProductGroupId: {type: "Int"},
            offset: {type: "Int", defaultValue: 1},
            limit: {type: "Int", defaultValue: 10},
            sellerModelNumber: {type: "String", defaultValue: ""},
            specs: {type: "String", defaultValue: ""},
            stocks: {type: "String", defaultValue: ""},
            price: {type: "String", defaultValue: ""},
            shippingDate: {type: "String", defaultValue: ""}
        ),
        {
            searchSellerGroup (sellerContactId: $sellerContactId,
                sellerModelId: $sellerModelId,
                sellerProductGroupId: $sellerProductGroupId,
                offset: $offset,
                limit: $limit,
                sellerModelNumber: $sellerModelNumber,
                specs: $specs,
                stocks: $stocks, 
                price: $price, 
                shippingDate: $shippingDate
            ) {
                models {
                  sellerProductGroupId
                  sellerProductGroupName
                  sellerModelId
                  sellerContactId
                  sellerModelNumber
                  price
                  stocks
                  shippingDate
                  salesMethodMaker
                  openPrice
                  salesMethod
                  salesMethodLabel
                  specs {
                      specId
                      specName
                      specValue
                  }
                  groupSpecs {
                    specId
                    specName
                    specValue
                  },
                  allModelSpecsIsPresentInGroup
                },
                groups {
                  sellerProductGroupId,
                  sellerProductGroupName,
                  sellerModelId
                },
                pageMetadata {
                  totalRecords
                  currentPage
                  recordsFrom
                  recordsTo
                  noOfPages
                  took
                  prevPage
                  nextPage
                  maxNoOfPageToDisplay
                }
            }
        }
    `
  },
  graphql`
      query SellerGroupModelListRefetchQuery($sellerContactId:String,
        $sellerModelId:String,
        $sellerProductGroupId:Int,
        $offset:Int,
        $limit:Int,
        $sellerModelNumber: String,
        $specs:String,
        $stocks: String, 
        $price: String, 
        $shippingDate: String
      ) {
          searchSeller {
              ...SellerGroupModelList_searchSeller @arguments(sellerContactId: $sellerContactId,
                  sellerModelId: $sellerModelId,
                  sellerProductGroupId:$sellerProductGroupId,
                  offset: $offset,
                  limit: $limit,
                  sellerModelNumber: $sellerModelNumber,
                  specs: $specs,
                  stocks: $stocks, 
                  price: $price, 
                  shippingDate: $shippingDate
              ),

          }
      }
  `
);
